<template>
  <Message icon="Alert" type="default" class="justify-center">
    <div class="max-w-[788px]">
      <template v-if="userStore.isSignedIn">
        <div
          v-if="ownerNotification"
          class="flex flex-col tablet-h:flex-row items-center gap-x-5"
        >
          <div class="font-secondary">
            {{ ownerFromName }} has requested to list you as one of the owners
            this copyright. Accept the request to be listed as an owner.
          </div>

          <div
            v-if="hasOwnerActions"
            class="w-full tablet-h:w-auto flex gap-x-2.5 mt-2 tablet-h:mt-0"
          >
            <UiButton
              class="w-full"
              :loading="loadingAccept"
              :disabled="loadingDecline"
              primary
              @click="handleAcceptClick(ownerNotification)"
            >
              Accept
            </UiButton>
            <UiButton
              class="w-full"
              :loading="loadingDecline"
              :disabled="loadingAccept"
              @click="handleDeclineClick(ownerNotification)"
            >
              Decline
            </UiButton>
          </div>
        </div>

        <div
          v-if="authorNotification"
          class="flex flex-col tablet-h:flex-row items-center gap-x-5"
        >
          <div class="font-secondary">
            {{ authorFromName }} has requested to list you as one of the authors
            this copyright. Accept the request to be listed as an author.
          </div>

          <div
            v-if="hasAuthorActions"
            class="w-full tablet-h:w-auto flex gap-x-2.5 mt-2 tablet-h:mt-0"
          >
            <UiButton
              class="w-full"
              :loading="loadingAccept"
              :disabled="loadingDecline"
              primary
              @click="handleAcceptClick(authorNotification)"
            >
              Accept
            </UiButton>
            <UiButton
              class="w-full"
              :loading="loadingDecline"
              :disabled="loadingAccept"
              @click="handleDeclineClick(authorNotification)"
            >
              Decline
            </UiButton>
          </div>
        </div>
      </template>

      <template v-else>
        <template v-if="props.file.settings.isPrivate">
          <span class="font-secondary">
            This copyright is private. To view it you must
            <RouterLink to="/login" class="text-link">sign in</RouterLink> or
            <span class="text-link" @click="handleRegisterClick">
              create an account
            </span>
            with the email you were invited with.
          </span>
        </template>

        <template v-else>
          <span class="font-secondary">
            To accept your invitation, you must
            <RouterLink to="/login" class="text-link">sign in</RouterLink> or
            <span class="text-link" @click="handleRegisterClick">
              create an account
            </span>
            with the email you were invited with.
          </span>
        </template>
      </template>
    </div>
  </Message>
</template>

<script setup lang="ts">
import {
  NotificationType,
  NotificationActionType,
} from '@/shared/graphql/router/graphql'
import type { CopyrightFile } from '@/features/copyright/composables/useCopyrightFile'
import {
  useAcceptInviteMutation,
  useDeclineInviteMutation,
} from '@/shared/graphql/legacy/generated'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { useUserStore } from '@/shared/stores/user'
import { Message, UiButton } from '@dacr/ui'
import { computed, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'

type FileNotification = NonNullable<CopyrightFile['notifications']>[number]

const props = defineProps<{
  file: CopyrightFile
}>()

const { loading: loadingAccept, mutate: acceptInvite } =
  useAcceptInviteMutation()

const { loading: loadingDecline, mutate: declineInvite } =
  useDeclineInviteMutation()

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const toastStore = useToastStore()

const ownerNotification = computed(() => {
  const notification = props.file.notifications?.find(
    (n) => n?.type === NotificationType.OwnerInvitePending
  )

  if (!notification) return null

  if (route.query.type === 'owner' || props.file.notifications?.length === 1) {
    return notification
  }

  return null
})

const authorNotification = computed(() => {
  const notification = props.file.notifications?.find(
    (n) => n?.type === NotificationType.AuthorInvitePending
  )

  if (!notification) return null

  if (route.query.type === 'author' || props.file.notifications?.length === 1) {
    return notification
  }

  return null
})

const ownerFromName = computed(() => {
  const details = ownerNotification.value?.details
  if (details?.__typename === 'InviteNotificationDetails') {
    return details.from.name
  }
  return ''
})

const authorFromName = computed(() => {
  const details = authorNotification.value?.details
  if (details?.__typename === 'InviteNotificationDetails') {
    return details.from.name
  }
  return ''
})

const hasOwnerActions = computed(() =>
  ownerNotification.value?.actions.some(
    (a) => a.type === NotificationActionType.AcceptOwnerRequest
  )
)

const hasAuthorActions = computed(() =>
  authorNotification.value?.actions.some(
    (a) => a.type === NotificationActionType.AcceptAuthorRequest
  )
)

const handleRegisterClick = () => {
  router.push(`/register?email=${route.query.email}`)
}

const handleAcceptClick = async (notification: FileNotification | null) => {
  if (!notification) return

  try {
    await acceptInvite({ id: notification.id })

    toastStore.showToast({
      type: ToastType.Success,
      content: 'You have successfully accepted the invite',
    })
  } catch (error) {
    console.error('Action failed', error)
  }
}

const handleDeclineClick = async (notification: FileNotification | null) => {
  if (!notification) return

  try {
    await declineInvite({ id: notification.id })
  } catch (error) {
    console.error('Action failed', error)
  }
}
</script>
